import { useCurrentEditor, useEditorState } from "@tiptap/react";
import { type FC, type RefObject, useMemo, useState } from "react";

import { useElementFromRef } from "../../hooks";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "../dropdown-menu";
import { Icon } from "../icon";
import { EditorButton } from "./editor-buttons";

interface EditorDropdownMenuTextBlockFormatProps {
  containerRef?: RefObject<HTMLDivElement | null>;
}

export const EditorDropdownMenuTextBlockFormat: FC<EditorDropdownMenuTextBlockFormatProps> = ({ containerRef }) => {
  const container = useElementFromRef(containerRef);
  const [isOpen, setIsOpen] = useState(false);
  const { editor } = useCurrentEditor();

  const editorState = useEditorState({
    editor,
    selector: (ctx) => ({
      isParagraph: ctx.editor?.isActive("paragraph") || false,
      isHeading1: ctx.editor?.isActive("heading", { level: 1 }) || false,
      isHeading2: ctx.editor?.isActive("heading", { level: 2 }) || false,
      isHeading3: ctx.editor?.isActive("heading", { level: 3 }) || false,
      isHeading4: ctx.editor?.isActive("heading", { level: 4 }) || false,
      isHeading5: ctx.editor?.isActive("heading", { level: 5 }) || false,
      isHeading6: ctx.editor?.isActive("heading", { level: 6 }) || false,
      isBulletList: ctx.editor?.isActive("bulletList") || false,
      isOrderedList: ctx.editor?.isActive("orderedList") || false,
      canSetParagraph: ctx.editor?.can().chain().setParagraph().run() ?? false,
      canToggleHeading1: ctx.editor?.can().chain().toggleHeading({ level: 1 }).run() ?? false,
      canToggleHeading2: ctx.editor?.can().chain().toggleHeading({ level: 2 }).run() ?? false,
      canToggleHeading3: ctx.editor?.can().chain().toggleHeading({ level: 3 }).run() ?? false,
      canToggleHeading4: ctx.editor?.can().chain().toggleHeading({ level: 4 }).run() ?? false,
      canToggleHeading5: ctx.editor?.can().chain().toggleHeading({ level: 5 }).run() ?? false,
      canToggleHeading6: ctx.editor?.can().chain().toggleHeading({ level: 6 }).run() ?? false,
      canToggleBulletList: ctx.editor?.can().chain().toggleBulletList().run() ?? false,
      canToggleOrderedList: ctx.editor?.can().chain().toggleOrderedList().run() ?? false,
    }),
  });

  const blockTextFormatOptions = useMemo(
    () => ({
      paragraph: {
        icon: "format_paragraph" as const,
        label: "Paragraph",
        onClick: () => editor?.chain().focus().setParagraph().run(),
      },
      heading1: {
        icon: "format_h1" as const,
        label: "Heading 1",
        onClick: () => editor?.chain().focus().toggleHeading({ level: 1 }).run(),
      },
      heading2: {
        icon: "format_h2" as const,
        label: "Heading 2",
        onClick: () => editor?.chain().focus().toggleHeading({ level: 2 }).run(),
      },
      heading3: {
        icon: "format_h3" as const,
        label: "Heading 3",
        onClick: () => editor?.chain().focus().toggleHeading({ level: 3 }).run(),
      },
      heading4: {
        icon: "format_h4" as const,
        label: "Heading 4",
        onClick: () => editor?.chain().focus().toggleHeading({ level: 4 }).run(),
      },
      heading5: {
        icon: "format_h5" as const,
        label: "Heading 5",
        onClick: () => editor?.chain().focus().toggleHeading({ level: 5 }).run(),
      },
      heading6: {
        icon: "format_h6" as const,
        label: "Heading 6",
        onClick: () => editor?.chain().focus().toggleHeading({ level: 6 }).run(),
      },
      bulletList: {
        icon: "format_list_bulleted" as const,
        label: "Bullet List",
        onClick: () => editor?.chain().focus().toggleBulletList().run(),
      },
      orderedList: {
        icon: "format_list_numbered" as const,
        label: "Ordered List",
        onClick: () => editor?.chain().focus().toggleOrderedList().run(),
      },
    }),
    [editor]
  );

  const selectedBlockTextFormatOption = useMemo(() => {
    if (editorState?.isHeading1) {
      return blockTextFormatOptions.heading1;
    }
    if (editorState?.isHeading2) {
      return blockTextFormatOptions.heading2;
    }
    if (editorState?.isHeading3) {
      return blockTextFormatOptions.heading3;
    }
    if (editorState?.isHeading4) {
      return blockTextFormatOptions.heading4;
    }
    if (editorState?.isHeading5) {
      return blockTextFormatOptions.heading5;
    }
    if (editorState?.isHeading6) {
      return blockTextFormatOptions.heading6;
    }
    if (editorState?.isBulletList) {
      return blockTextFormatOptions.bulletList;
    }
    if (editorState?.isOrderedList) {
      return blockTextFormatOptions.orderedList;
    }

    return blockTextFormatOptions.paragraph;
  }, [editorState, blockTextFormatOptions]);

  const handleCloseAutoFocus = (event: Event) => {
    event.preventDefault();
  };

  if (!editor) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <EditorButton icon={selectedBlockTextFormatOption.icon} className="pr-2">
          {selectedBlockTextFormatOption.label} <Icon icon="keyboard_arrow_down" className="size-4" />
        </EditorButton>
      </DropdownMenuTrigger>
      {/* NOTE: We need to conditionally render the dropdown menu content to ensure the dropdown menu is properly
      positioned with respect to the container ref when the component is mounted or refreshed. */}
      {isOpen && (
        <DropdownMenuContent align="start" onCloseAutoFocus={handleCloseAutoFocus} container={container}>
          {Object.entries(blockTextFormatOptions).map(([key, value]) => (
            <DropdownMenuCheckboxItem
              key={key}
              checked={selectedBlockTextFormatOption === value}
              onSelect={value.onClick}
              indicatorPosition="end"
            >
              <Icon icon={value.icon} className="text-base" />
              <span className="text-sm">{value.label}</span>
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
};
